'use client';

import { useState } from 'react';
import ReportTemplates from './ReportTemplates';
import RecentReportsTable from './RecentReportsTable';
import ScheduleReportForm from './ScheduleReportForm';
import ReportSummaryCharts from './ReportSummaryCharts';

type Tab = 'overview' | 'templates' | 'scheduled' | 'history';

export default function ReportsHub() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [frameworkFilter, setFrameworkFilter] = useState<'all' | 'pci-dss' | 'iso27001' | 'soc2' | 'operational'>('all');

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'templates', label: 'Report Templates', count: 12 },
    { id: 'scheduled', label: 'Scheduled Reports', count: 4 },
    { id: 'history', label: 'Report History', count: 47 },
  ];

  return (
    <div className="flex flex-col gap-5">
      {/* Tab Nav */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
              ${activeTab === tab.id
                ? 'border-rose-500 text-rose-400' :'border-transparent text-zinc-500 hover:text-zinc-300'
              }
            `}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === tab.id ? 'bg-rose-500/20 text-rose-400' : 'bg-zinc-800 text-zinc-600'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Framework Filter (shown on templates/overview tabs) */}
      {(activeTab === 'overview' || activeTab === 'templates') && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-600 font-medium uppercase tracking-wide">Framework:</span>
          {(['all', 'pci-dss', 'iso27001', 'soc2', 'operational'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFrameworkFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                frameworkFilter === f
                  ? 'bg-rose-600 text-white' :'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
              }`}
            >
              {f === 'all' ? 'All' : f === 'pci-dss' ? 'PCI-DSS' : f === 'iso27001' ? 'ISO 27001' : f === 'soc2' ? 'SOC 2' : 'Operational'}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'overview' && (
        <>
          <ReportSummaryCharts />
          <RecentReportsTable limit={5} />
        </>
      )}
      {activeTab === 'templates' && <ReportTemplates frameworkFilter={frameworkFilter} />}
      {activeTab === 'scheduled' && <ScheduleReportForm />}
      {activeTab === 'history' && <RecentReportsTable limit={50} />}
    </div>
  );
}